import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  acceptPartnership,
  declinePartnership,
  endPartnership,
  invitePartner,
  listPartnerChannels,
  listPartnerships,
  getPartnerChannel,
  searchPartnerDirectory,
} from "@/lib/partnerApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import PartnerChannelMembersPanel, {
  partnerMembersDescription,
} from "@/components/dm/PartnerChannelMembersPanel";
import PartnerChannelSettingsForm, {
  formatRetentionDays,
} from "@/components/dm/PartnerChannelSettingsForm";
import { useOptionalNotificationsSocket } from "@/scripts/NotificationsSocketContext";
import { useOptionalPartnerSocket } from "@/scripts/PartnerSocketContext";
import { X } from "lucide-react";

const RETENTION_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "180", label: "180 days" },
  { value: "365", label: "365 days" },
  { value: "null", label: "Indefinite (Business+)" },
];

function retentionLabel(value) {
  return (
    RETENTION_OPTIONS.find((o) => o.value === value)?.label || `${value} days`
  );
}

export default function OrgPartnerSpacesPanel({ orgId, accessToken }) {
  const [partnerships, setPartnerships] = useState([]);
  const [channels, setChannels] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orgQuery, setOrgQuery] = useState("");
  const [orgResults, setOrgResults] = useState([]);
  const [orgSearchBusy, setOrgSearchBusy] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [channelTitle, setChannelTitle] = useState("Partner channel");
  const [retention, setRetention] = useState("90");
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [channelDetail, setChannelDetail] = useState(null);
  const searchSeq = useRef(0);
  const notificationsSocket = useOptionalNotificationsSocket();
  const notificationsSubscribe = notificationsSocket?.subscribe;
  const partnerSocket = useOptionalPartnerSocket();
  const partnerSubscribe = partnerSocket?.subscribe;

  const refresh = useCallback(async () => {
    if (!accessToken || !orgId) return;
    setError(null);
    try {
      const [p, c] = await Promise.all([
        listPartnerships(accessToken, orgId),
        listPartnerChannels(accessToken, orgId),
      ]);
      setPartnerships(p);
      setChannels(c);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load partner spaces"
      );
    }
  }, [accessToken, orgId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!notificationsSubscribe && !partnerSubscribe) return undefined;
    const onEvent = (event, payload) => {
      if (
        event === "partnership.updated" ||
        event === "partner.channel.updated"
      ) {
        refresh();
        if (
          event === "partner.channel.updated" &&
          payload?.channel &&
          selectedChannelId &&
          Number(payload.channel_id) === Number(selectedChannelId)
        ) {
          setChannelDetail((prev) => ({
            ...prev,
            ...payload.channel,
            members: payload.channel.members ?? prev?.members,
          }));
        }
      }
    };
    const unsubN = notificationsSubscribe
      ? notificationsSubscribe(onEvent)
      : undefined;
    const unsubP = partnerSubscribe ? partnerSubscribe(onEvent) : undefined;
    return () => {
      unsubN?.();
      unsubP?.();
    };
  }, [
    notificationsSubscribe,
    partnerSubscribe,
    refresh,
    selectedChannelId,
  ]);

  useEffect(() => {
    if (selectedOrg || !accessToken || !orgId) {
      setOrgResults([]);
      setOrgSearchBusy(false);
      return undefined;
    }
    const q = orgQuery.trim();
    if (q.length < 2) {
      setOrgResults([]);
      setOrgSearchBusy(false);
      return undefined;
    }
    const seq = ++searchSeq.current;
    setOrgSearchBusy(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchPartnerDirectory(
          accessToken,
          orgId,
          q,
          12
        );
        if (seq === searchSeq.current) {
          setOrgResults(results);
        }
      } catch {
        if (seq === searchSeq.current) {
          setOrgResults([]);
        }
      } finally {
        if (seq === searchSeq.current) {
          setOrgSearchBusy(false);
        }
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [orgQuery, selectedOrg, accessToken, orgId]);

  useEffect(() => {
    if (!selectedChannelId || !accessToken) {
      setChannelDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ch = await getPartnerChannel(
          accessToken,
          orgId,
          selectedChannelId
        );
        if (!cancelled) setChannelDetail(ch);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedChannelId, accessToken, orgId]);

  const applyChannelDetail = (next) => {
    if (!next) return;
    setChannelDetail((prev) => ({
      ...prev,
      ...next,
      members: next.members ?? prev?.members,
    }));
    setChannels((prev) =>
      prev.map((c) =>
        c.channel_id === next.channel_id
          ? {
              ...c,
              title: next.title ?? c.title,
              retention_days: next.retention_days ?? c.retention_days,
              legal_hold: next.legal_hold ?? c.legal_hold,
              status: next.status ?? c.status,
            }
          : c
      )
    );
  };

  const run = async (fn, successMessage) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res?.channel) applyChannelDetail(res.channel);
      await refresh();
      if (selectedChannelId && !res?.channel) {
        const ch = await getPartnerChannel(
          accessToken,
          orgId,
          selectedChannelId
        );
        setChannelDetail(ch);
      }
      if (successMessage) toastSuccess(successMessage);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Request failed";
      setError(msg);
      toastError(err, msg);
    } finally {
      setBusy(false);
    }
  };

  const pendingIncoming = partnerships.filter(
    (p) => p.status === "pending" && p.direction === "incoming"
  );
  const pendingOutgoing = partnerships.filter(
    (p) => p.status === "pending" && p.direction === "outgoing"
  );
  const active = partnerships.filter((p) => p.status === "active");

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Partner spaces</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Mutually consented channels shared with another organization. People
          stay in their own workplace — only the shared room is dual-mounted.
        </p>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <Card>
        <CardHeader>
          <CardTitle>Invite organization</CardTitle>
          <CardDescription>
            Search for an organization by name, pick it from the results, then
            send the invitation. Both sides get a Partner channel with the agreed
            retention after accept.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="partner-org-search">Organization</Label>
            {selectedOrg ? (
              <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
                <OrganizationAvatar
                  org={selectedOrg}
                  className="h-9 w-9"
                  fallbackClassName="text-xs"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {selectedOrg.name}
                  </p>
                  {selectedOrg.partnership_status ? (
                    <p className="text-xs text-muted-foreground">
                      Already {selectedOrg.partnership_status}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Selected</p>
                  )}
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0"
                  disabled={busy}
                  onClick={() => {
                    setSelectedOrg(null);
                    setOrgQuery("");
                    setOrgResults([]);
                  }}
                  aria-label="Clear selected organization"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="partner-org-search"
                  value={orgQuery}
                  onChange={(e) => setOrgQuery(e.target.value)}
                  placeholder="Search organizations by name…"
                  autoComplete="off"
                  disabled={busy}
                />
                {(orgSearchBusy ||
                  orgResults.length > 0 ||
                  orgQuery.trim().length >= 2) && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-background shadow-md max-h-56 overflow-y-auto">
                    {orgSearchBusy ? (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        Searching…
                      </p>
                    ) : orgResults.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        No organizations found
                      </p>
                    ) : (
                      <ul>
                        {orgResults.map((o) => {
                          const blocked = !!o.partnership_status;
                          return (
                            <li key={o.org_id}>
                              <button
                                type="button"
                                disabled={blocked || busy}
                                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/60 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => {
                                  setSelectedOrg(o);
                                  setOrgQuery("");
                                  setOrgResults([]);
                                }}
                              >
                                <OrganizationAvatar
                                  org={o}
                                  className="h-8 w-8"
                                  fallbackClassName="text-[10px]"
                                />
                                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                                  {o.name}
                                </span>
                                {blocked ? (
                                  <Badge
                                    variant="secondary"
                                    className="shrink-0 font-normal"
                                  >
                                    {o.partnership_status}
                                  </Badge>
                                ) : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="partner-title">Channel title</Label>
            <Input
              id="partner-title"
              value={channelTitle}
              onChange={(e) => setChannelTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="partner-retention">Retention</Label>
            <select
              id="partner-retention"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={retention}
              onChange={(e) => setRetention(e.target.value)}
            >
              {RETENTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {selectedOrg && !selectedOrg.partnership_status ? (
            <p className="text-xs text-muted-foreground">
              Invite <span className="font-medium text-foreground">{selectedOrg.name}</span>{" "}
              to{" "}
              <span className="font-medium text-foreground">
                {channelTitle.trim() || "Partner channel"}
              </span>{" "}
              with {retentionLabel(retention)} retention.
            </p>
          ) : null}
          <Button
            disabled={
              busy ||
              !selectedOrg ||
              !!selectedOrg.partnership_status
            }
            onClick={() =>
              run(async () => {
                await invitePartner(accessToken, orgId, {
                  target_org_id: selectedOrg.org_id,
                  channel_title: channelTitle.trim() || "Partner channel",
                  retention_days:
                    retention === "null" ? null : Number(retention),
                });
                setSelectedOrg(null);
                setOrgQuery("");
                setOrgResults([]);
              })
            }
          >
            Send invitation
          </Button>
        </CardContent>
      </Card>

      {pendingIncoming.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Incoming invitations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingIncoming.map((p) => (
              <div
                key={p.partnership_id}
                className="flex flex-wrap items-center gap-2 justify-between border-b border-border pb-3 last:border-0"
              >
                <div>
                  <p className="text-sm font-medium">
                    {p.partner_org?.name || p.partner_org_id}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {p.channel_title} · retention{" "}
                    {formatRetentionDays(p.proposed_retention_days)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        acceptPartnership(accessToken, orgId, p.partnership_id)
                      )
                    }
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        declinePartnership(accessToken, orgId, p.partnership_id)
                      )
                    }
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {pendingOutgoing.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Outgoing invitations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingOutgoing.map((p) => (
              <div key={p.partnership_id} className="text-sm">
                Waiting on {p.partner_org?.name || p.partner_org_id} ·{" "}
                {p.channel_title}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Active partner channels</CardTitle>
          <CardDescription>
            Open in Messages under the Partner section.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {channels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No partner channels yet.
            </p>
          ) : (
            channels.map((c) => (
              <div
                key={c.channel_id}
                className="flex flex-wrap items-center gap-2 justify-between border-b border-border pb-3 last:border-0"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    <Badge variant="outline">Partner</Badge>
                    {c.status !== "active" ? (
                      <Badge variant="secondary">{c.status}</Badge>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    with {c.partner_org?.name} · retention{" "}
                    {formatRetentionDays(c.retention_days)}
                    {c.legal_hold ? " · legal hold" : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" asChild>
                    <Link
                      to={`/o/${encodeURIComponent(orgId)}/messages/partner/${c.channel_id}`}
                    >
                      Open
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedChannelId(c.channel_id)}
                  >
                    Manage
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {channelDetail ? (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div className="min-w-0 space-y-1.5">
              <CardTitle className="truncate">
                Manage: {channelDetail.title}
              </CardTitle>
              <CardDescription>
                {partnerMembersDescription(channelDetail, orgId)} Retention
                changes and legal-hold releases need dual consent.
              </CardDescription>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="shrink-0"
              onClick={() => setSelectedChannelId(null)}
            >
              Close
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            <PartnerChannelMembersPanel
              channel={channelDetail}
              viewerOrgId={orgId}
              channelId={channelDetail.channel_id}
              accessToken={accessToken}
              onChannelChange={applyChannelDetail}
              active={!!selectedChannelId}
            />

            <div className="border-t border-border pt-4">
              <PartnerChannelSettingsForm
                channel={channelDetail}
                orgId={orgId}
                channelId={channelDetail.channel_id}
                accessToken={accessToken}
                onChannelChange={applyChannelDetail}
                showExport
              />
            </div>

            {active
              .filter((p) => p.partnership_id === channelDetail.partnership_id)
              .map((p) => (
                <Button
                  key={p.partnership_id}
                  size="sm"
                  variant="destructive"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () =>
                        endPartnership(
                          accessToken,
                          orgId,
                          p.partnership_id,
                          "ended_by_admin"
                        ),
                      "Partnership ended"
                    )
                  }
                >
                  End partnership (soft-close)
                </Button>
              ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
